import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import catalog from '../assets/forms.json';
import '../styles/RolePermissions.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faPlus,
  faSearch,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

const GROUPS = catalog.groups || [];

const itemMatchesSearch = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  if (item.name?.toLowerCase().includes(q) || item.key?.toLowerCase().includes(q)) {
    return true;
  }
  return (item.children || []).some((child) => itemMatchesSearch(child, query));
};

const selfMatchesSearch = (item, query) => {
  if (!query) return false;
  const q = query.toLowerCase();
  return item.name?.toLowerCase().includes(q) || item.key?.toLowerCase().includes(q);
};

const isActionGranted = (permissions, key, action) =>
  permissions?.[key]?.[action] === 'true';

const itemHasOwnGrant = (item, permissions) =>
  (item.permissions || []).some((action) => isActionGranted(permissions, item.key, action));

const itemHasAnyGrant = (item, permissions) => {
  if (itemHasOwnGrant(item, permissions)) return true;
  return (item.children || []).some((child) => itemHasAnyGrant(child, permissions));
};

const collectKeysWithChanges = (current, original) => {
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(original || {}),
  ]);
  const changed = new Set();
  keys.forEach((key) => {
    const a = current?.[key] || {};
    const b = original?.[key] || {};
    const actions = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const action of actions) {
      if ((a[action] || 'false') !== (b[action] || 'false')) {
        changed.add(key);
        break;
      }
    }
  });
  return changed;
};

const treeContainsKey = (item, keys) => {
  if (keys.has(item.key)) return true;
  return (item.children || []).some((child) => treeContainsKey(child, keys));
};

const filterTree = (item, query, grantFilter, permissions) => {
  if (query && !itemMatchesSearch(item, query)) return null;

  const children = (item.children || [])
    .map((child) => filterTree(child, query, 'all', permissions))
    .filter(Boolean);

  if (grantFilter === 'granted' && !itemHasAnyGrant(item, permissions)) {
    return null;
  }
  if (grantFilter === 'ungranted' && itemHasAnyGrant(item, permissions)) {
    return null;
  }

  if (query && !selfMatchesSearch(item, query) && children.length === 0) {
    return null;
  }

  return { ...item, children };
};

const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [savedPermissions, setSavedPermissions] = useState({});
  const [newRoleName, setNewRoleName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [grantFilter, setGrantFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [status, setStatus] = useState(null);

  const rolesAPI = `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_ROLES}`;

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(rolesAPI);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setStatus({ type: 'error', message: 'Failed to load roles.' });
    }
  };

  const changedKeys = useMemo(
    () => collectKeysWithChanges(permissions, savedPermissions),
    [permissions, savedPermissions]
  );
  const isDirty = changedKeys.size > 0;

  const normalizedQuery = searchQuery.trim();

  const visibleGroups = useMemo(() => {
    return GROUPS.map((group) => {
      const forms = (group.forms || [])
        .map((form) => filterTree(form, normalizedQuery, grantFilter, permissions))
        .filter(Boolean);
      return { ...group, forms };
    }).filter((group) => group.forms.length > 0);
  }, [normalizedQuery, grantFilter, permissions]);

  const groupSummaries = useMemo(() => {
    const summaries = {};
    GROUPS.forEach((group) => {
      const total = (group.forms || []).length;
      const granted = (group.forms || []).filter((form) =>
        itemHasAnyGrant(form, permissions)
      ).length;
      summaries[group.id] = { granted, total };
    });
    return summaries;
  }, [permissions]);

  useEffect(() => {
    if (normalizedQuery || grantFilter !== 'all') {
      const next = {};
      visibleGroups.forEach((group) => {
        next[group.id] = true;
      });
      setExpandedGroups(next);
      return;
    }

    const next = {};
    GROUPS.forEach((group) => {
      const hasUnsaved = (group.forms || []).some((form) =>
        treeContainsKey(form, changedKeys)
      );
      next[group.id] = hasUnsaved;
    });
    setExpandedGroups(next);
  }, [normalizedQuery, grantFilter, selectedRole?.id]);

  const showStatus = (type, message) => {
    setStatus({ type, message });
  };

  const handleRoleChange = (role) => {
    if (isDirty && selectedRole?.id !== role.id) {
      const leave = window.confirm(
        'You have unsaved permission changes. Switch roles without saving?'
      );
      if (!leave) return;
    }
    setSelectedRole(role);
    const nextPermissions = role.permissions || {};
    setPermissions(nextPermissions);
    setSavedPermissions(nextPermissions);
    setSearchQuery('');
    setGrantFilter('all');
    setStatus(null);
  };

  const handlePermissionChange = (formKey, permissionType, value) => {
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [formKey]: {
        ...prevPermissions[formKey],
        [permissionType]: value,
      },
    }));
    setStatus(null);
  };

  const handleToggleAll = (item, checked) => {
    const actions = item.permissions || [];
    if (!item.key || actions.length === 0) return;
    const value = checked ? 'true' : 'false';
    setPermissions((prevPermissions) => ({
      ...prevPermissions,
      [item.key]: actions.reduce((acc, action) => {
        acc[action] = value;
        return acc;
      }, { ...(prevPermissions[item.key] || {}) }),
    }));
    setStatus(null);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setIsSaving(true);
    try {
      await axios.put(`${rolesAPI}/${selectedRole.id}`, {
        ...selectedRole,
        permissions,
      });
      setSavedPermissions(permissions);
      setRoles((prev) =>
        prev.map((role) =>
          role.id === selectedRole.id ? { ...role, permissions } : role
        )
      );
      setSelectedRole((prev) => (prev ? { ...prev, permissions } : prev));
      showStatus('success', 'Permissions saved.');
    } catch (error) {
      console.error('Error saving permissions:', error);
      showStatus('error', 'Failed to save permissions.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRole = async () => {
    const name = newRoleName.trim();
    if (!name) {
      showStatus('error', 'Enter a role name first.');
      return;
    }
    if (isDirty) {
      const leave = window.confirm(
        'You have unsaved permission changes. Add a role without saving?'
      );
      if (!leave) return;
    }
    try {
      const newRole = {
        name,
        permissions: {},
      };
      const response = await axios.post(`${rolesAPI}`, newRole);
      const created = response.data;
      setRoles([...roles, created]);
      setNewRoleName('');
      setSelectedRole(created);
      setPermissions(created.permissions || {});
      setSavedPermissions(created.permissions || {});
      setSearchQuery('');
      setGrantFilter('all');
      showStatus('success', `Role "${name}" added.`);
    } catch (error) {
      console.error('Error adding role:', error);
      showStatus('error', 'Failed to add role.');
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const expandAllVisible = () => {
    const next = { ...expandedGroups };
    visibleGroups.forEach((group) => {
      next[group.id] = true;
    });
    setExpandedGroups(next);
  };

  const collapseAll = () => {
    const next = {};
    GROUPS.forEach((group) => {
      next[group.id] = false;
    });
    setExpandedGroups(next);
  };

  const PermissionItem = ({ item, level = 0 }) => {
    const actions = Array.isArray(item.permissions) ? item.permissions : [];
    const grantedCount = actions.filter((action) =>
      isActionGranted(permissions, item.key, action)
    ).length;
    const allChecked = actions.length > 0 && grantedCount === actions.length;
    const someChecked = grantedCount > 0 && grantedCount < actions.length;
    const highlighted = selfMatchesSearch(item, normalizedQuery);
    const children = item.children || [];

    return (
      <div
        className={`permission-item level-${level}${highlighted ? ' search-match' : ''}`}
      >
        <div className="permission-header">
          <div className="permission-title-block">
            <h3 className={`permission-title level-${level}`}>{item.name}</h3>
            {item.key && <span className="permission-key">{item.key}</span>}
          </div>
          {actions.length > 0 && (
            <label className="permission-label permission-all">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = someChecked;
                }}
                onChange={(e) => handleToggleAll(item, e.target.checked)}
              />
              <span className="permission-type">All</span>
            </label>
          )}
        </div>
        <div className="permission-body">
          {actions.length > 0 && (
            <div className="permission-checkboxes">
              {actions.map((permissionType) => (
                <label key={permissionType} className="permission-label">
                  <input
                    type="checkbox"
                    checked={isActionGranted(permissions, item.key, permissionType)}
                    onChange={(e) =>
                      handlePermissionChange(
                        item.key,
                        permissionType,
                        e.target.checked.toString()
                      )
                    }
                  />
                  <span className="permission-type">{permissionType}</span>
                </label>
              ))}
            </div>
          )}
          {children.length > 0 && (
            <div className={`nested-permissions level-${level + 1}`}>
              {children.map((child) => (
                <PermissionItem key={child.key || child.name} item={child} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="role-permissions-container">
      <div className="sidebar">
        <h2>Roles</h2>
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => handleRoleChange(role)}
            className={`role-button ${selectedRole?.id === role.id ? 'active' : ''}`}
          >
            {role.name}
          </button>
        ))}
        <div className="add-role-container">
          <input
            type="text"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddRole();
            }}
            placeholder="New Role Name"
            className="add-role-input"
          />
          <button onClick={handleAddRole} className="add-role-button">
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>

      {selectedRole ? (
        <div className="permissions-section">
          <div className="permissions-toolbar">
            <div className="permissions-heading">
              <h2>Permissions for {selectedRole.name}</h2>
              {isDirty && <span className="unsaved-badge">Unsaved changes</span>}
            </div>
            <div className="permissions-search-row">
              <label className="permissions-search">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Find a screen or action..."
                  aria-label="Find a screen or action"
                />
              </label>
              <div className="grant-filter" role="group" aria-label="Filter by granted">
                <button
                  type="button"
                  className={grantFilter === 'all' ? 'active' : ''}
                  onClick={() => setGrantFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={grantFilter === 'granted' ? 'active' : ''}
                  onClick={() => setGrantFilter('granted')}
                >
                  Granted
                </button>
                <button
                  type="button"
                  className={grantFilter === 'ungranted' ? 'active' : ''}
                  onClick={() => setGrantFilter('ungranted')}
                >
                  Not granted
                </button>
              </div>
              <div className="expand-actions">
                <button type="button" onClick={expandAllVisible}>
                  Expand all
                </button>
                <button type="button" onClick={collapseAll}>
                  Collapse all
                </button>
              </div>
            </div>
          </div>

          <div className="permissions-groups">
            {visibleGroups.length === 0 ? (
              <p className="no-results">No screens match that search.</p>
            ) : (
              visibleGroups.map((group) => {
                const expanded = !!expandedGroups[group.id];
                const summary = groupSummaries[group.id] || {
                  granted: 0,
                  total: group.forms.length,
                };
                return (
                  <section key={group.id} className="permission-group">
                    <button
                      type="button"
                      className="permission-group-header"
                      onClick={() => toggleGroup(group.id)}
                      aria-expanded={expanded}
                    >
                      <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                      <span className="permission-group-name">{group.name}</span>
                      <span className="permission-group-summary">
                        {summary.granted} of {summary.total} screens granted
                      </span>
                    </button>
                    {expanded && (
                      <div className="permission-group-body">
                        {group.forms.map((form) => (
                          <PermissionItem key={form.key} item={form} level={0} />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })
            )}
          </div>

          <div className="permissions-save-bar">
            {status && (
              <div className={`status-message ${status.type}`} role="status">
                {status.message}
              </div>
            )}
            <button
              onClick={handleSavePermissions}
              className={`btn-save ${isSaving ? 'saving' : ''}`}
              disabled={isSaving || !isDirty}
            >
              <FontAwesomeIcon icon={faSave} /> {isSaving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>
        </div>
      ) : (
        <div className="permissions-section empty-state">
          {status && (
            <div className={`status-message ${status.type}`} role="status">
              {status.message}
            </div>
          )}
          <p>Select a role to set permissions.</p>
        </div>
      )}
    </div>
  );
};

export default RolePermissions;
